<script setup>

import {$dateFormatterWithDay, subText} from "@/mixins/Helpers";
import Img from "@/components/Img.vue";
import Input from "@/components/Input.vue";
import {ref} from "vue";
import { useStore } from 'vuex'
import {useLocales} from "@/composables/locales";
import {useI18n} from "vue-i18n";

const { t } = useI18n({ useScope: 'global' })
const store = useStore()
const { locale } = useLocales()

const sortBy = defineModel('sortBy', {default: {name: 'date', sort: 'desc'}})
const selectedRows = defineModel('selectedRows', {default: []})

const emits = defineEmits(['handleReadMail', 'handleClickRow', 'handleDoubleClickRow'])

const props = defineProps({
  mails: { type: Array, required: true, default: [] },
  currentMail: Object,
  currentFolder: {type: String, required: true},
  isSendedTab: { type: Boolean, default: false },
  showSchedule: { type: Boolean, default: false },
})

const draggedMailId = ref('')
const checkAll = ref(false)

const delay = ref(700)
const clicks = ref(0)
const timer = ref(null)

function setCurrentAllRow() {
  if(checkAll.value){
    props.mails?.filter((item) => {
      selectedRows.value.push(item.id)
    });
  }else{
    selectedRows.value = []
  }
}

function setCurrentRow(mail) {
  console.log("here", selectedRows.value)
  if(!selectedRows.value.length) loadCurrentMail(mail)
  if (selectedRows.value.includes(mail.id)) {
    const rest = selectedRows.value?.filter(item => item !== mail.id);
    console.log("selectedRows.value.length", rest.length)
    if(rest.length === 1) {
      console.log("selectedRows.value.length", rest[0])
      loadCurrentMail({id: rest[0]})
    }
    selectedRows.value = rest
  } else {
    selectedRows.value.push(mail.id)
  }
}

function setSort(elt1, elt2) {
  sortBy.value = {
    name: elt1,
    sort: elt2
  }
}

function displayReceiversName(receivers) {
  return receivers?.map(to => to.full.toString().replace(/<\s*[^<>]+?\s*>/, ''))?.toString() || ''
}

function onDragStart(event, mail) {
  event.dataTransfer.setData("text/plain", mail.subject);
  let name = ''
  if(props.isSendedTab || props.showSchedule){
    name = subText(displayReceiversName(mail.to), 20)
  }else{
    name = mail.from_name || mail.from
  }
  draggedMailId.value = mail.id
  const dragGhost = document.createElement("div");
  dragGhost.textContent = name + ' ' + mail.subject;

  dragGhost.style.position = "absolute";
  dragGhost.style.top = "-1000px";
  dragGhost.style.padding = "8px 12px";
  dragGhost.style.background = "#333";
  dragGhost.style.borderRadius = "8px";
  dragGhost.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
  dragGhost.style.fontSize = "14px";
  dragGhost.style.color = "#fff";
  dragGhost.style.fontFamily = "Arial, sans-serif";

  document.body.appendChild(dragGhost);
  event.dataTransfer.setDragImage(dragGhost, 0, 0);

  setTimeout(() => {
    document.body.removeChild(dragGhost);
  }, 0);
}

async function changeStatus(mail) {
  mail.isSeen = !mail.isSeen;
  if(mail.isSeen){
    await store.dispatch('mails/markAsRead', {email_ids: [mail.id], folder: props.currentFolder}).then(() => emits('handleReadMail', false))
    window.toastr['success'](t('mail.mark_as_read_successfully'))
  }else{
    await store.dispatch('mails/markAsUnread', {email_ids: [mail.id], folder: props.currentFolder}).then(() => emits('handleReadMail', true))
    window.toastr['success'](t('mail.mark_as_unread_successfully'))
  }
}

async function importantOrNot(mail) {
  mail.isFlagged = !mail.isFlagged;
  if(mail.isFlagged){
    await store.dispatch('mails/markAsImportant', {email_ids: [mail.id], folder: props.currentFolder})
    window.toastr['success'](t('mail.mark_as_important_successfully'))
  }else{
    await store.dispatch('mails/markAsNotImportant', {email_ids: [mail.id], folder: props.currentFolder})
    window.toastr['success'](t('mail.mark_as_not_important_successfully'))
  }
}

function handleClickRow(data) {
  clicks.value++;
  if (clicks.value === 1) {
    timer.value = setTimeout( () => {
      emits('handleClickRow', data)
      selectedRows.value = [data.id]
      clicks.value = 0
    }, delay.value);
  } else {
    clearTimeout(timer.value);
    emits('handleDoubleClickRow', data)
    clicks.value = 0
  }
}

async function loadCurrentMail(detail) {
  if(props.currentFolder === 'schedule'){
    await store.dispatch('mails/getScheduleMail', detail.id)
  }else{
    await store.dispatch('mails/getMail', {folder: props.currentFolder, uid: detail.id})
  }
}



</script>

<template>
  <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4">
    <thead>
    <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
      <th class="w-80px text-start">
        <div class="d-flex">
          <input class="me-3" type="checkbox" v-model="checkAll" @change="setCurrentAllRow"/>
          <i class="fas fa-eye fs-4" style="color: #303D4E;"></i>
        </div>
      </th>
      <th class="w-300px text-start">
        {{ isSendedTab || showSchedule ? $t('mail.a') : $t('mail.of') }}
      </th>
      <th class="min-w-250px text-start">
        {{ $t('mail.subject') }}
      </th>
      <th class="w-5px text-start">
        <i class="fas fa-paperclip fs-5" style="color: #303D4E;"></i>
      </th>
      <th class="w-200px text-start">
        {{ $t('mail.date') }}
        <i class="fas fa-sort-amount-down fs-4 cursor-pointer" v-if="sortBy.name === 'date' && sortBy.sort === 'desc'" @click.prevent="setSort('date', 'asc')" style="color: #303D4E;"></i>
        <i class="fas fa-sort-amount-up fs-4 cursor-pointer" v-else @click.prevent="setSort('date', 'desc')" style="color: #303D4E;"></i>
      </th>
    </tr>
    </thead>
    <tbody v-if="mails?.length > 0">
    <tr @click="handleClickRow(mail)" draggable="true" @dragstart="(event) => onDragStart(event, mail)"  v-for="(mail, index) in mails" :key="index" :class="!mail.isSeen ? 'active_tr' : currentMail?.id === mail.id ? 'selected_tr' : ''" style="cursor: pointer;">
      <td class="py-3">
        <div class="d-flex">
          <input class="me-5 custom-checkbox" type="checkbox" :title="$t('mail.selected')" :checked="selectedRows.includes(mail.id)" style="cursor: pointer;" @click.stop="setCurrentRow(mail)"/>
          <input class="me-5 custom-radio" type="checkbox" v-if="!showSchedule" :title="$t('mail.read_or_not')" :checked="!mail.isSeen" style="cursor: pointer;" @click.stop="changeStatus(mail)"/>
          <i class="fas fa-star fs-4" v-if="!showSchedule" :title="$t('mail.important_or_not')" :class="mail.isFlagged ? 'text-primary' : 'text-light-primary'" @click.stop="importantOrNot(mail)"></i>
        </div>
      </td>
      <td class="py-3">
        <span v-if="isSendedTab || showSchedule"
              :title="displayReceiversName(mail.to)">
          {{ subText(displayReceiversName(mail.to), 20) }}
        </span>

        <span v-else> {{ mail.from_name || mail.from }} </span>
      </td>
      <td class="py-3">
        {{decodeURIComponent(mail.subject)}}
      </td>
      <td class="py-3">
        <i class="fas fa-paperclip fs-5" style="color: #303D4E;" v-if="mail.hasFile"></i>
      </td>
      <td class="py-3">
        {{$dateFormatterWithDay(mail.date, locale, true)}}
      </td>
    </tr>
    </tbody>
    <tbody v-else>
    <tr>
      <td colspan="5" class="text-center">
        <Img filename="empty.png" alt="" class="mw-100 h-125px h-sm-175px" />
        <div class="text-gray-400 fs-4 fw-bold">{{ $t('no_data_found') }}</div>
      </td>
    </tr>
    </tbody>
  </table>
</template>

<style scoped>
.dropdown-toggle::after {
  display: none !important;
}

.custom-checkbox {
  appearance: none;
  width: 15px;
  height: 15px;
  background-color: var(--bs-light-primary);
  border-radius: 4px;
  cursor: pointer;
  position: relative;
}

.custom-checkbox:checked {
  background-color: var(--bs-primary);
}

.custom-checkbox:checked::after {
  content: '✔';
  color: white;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
}

.custom-radio {
  appearance: none;
  width: 13px;
  height: 13px;
  background-color: var(--bs-light-primary);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
}

.text-light-primary {
  color: var(--bs-light-primary) !important;
}

.custom-radio:checked {
  background-color: var(--bs-primary);
}

.custom-radio:checked::after {
  content: '';
  color: white;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
}

.active_tr {
  margin: 10px 50px !important;
  font-weight: 600 !important;
}

.selected_tr {
  background-color: var(--bs-light-primary);
  margin: 10px 50px !important;
}

</style>